import {
  Container,
  YStack,
  SizableText,
  ScrollView,
  XStack,
  Spinner,
  Stack,
  colors,
  Box,
} from '@my/ui'
import React, { useState } from 'react'
import { MainLayout, Meta, NoAccess, SectionTitle } from 'app/components'
import { GroupCardBasic, GroupCardHorizontal } from '../../containers'
import { User } from 'app/types'
import { UserGroupJoinRequestsQueryOutput, UserGroupsQueryOutput } from '../../queries'
import { RefreshControl } from 'react-native'
import {
  canJoinGroup,
  canLeaveGroup,
  hasJoinedGroup,
  hasPendingGroupMembershipRequest,
} from '../../helpers'
import { SuggestedGroupsQueryOutput } from './queries'
import { GroupNoResults } from '../../components'
import { PlusCircle } from '@tamagui/lucide-icons'
import { GroupCreationWizard } from './containers'
import { GroupVisibility } from 'app/types/GroupVisibility'

export interface GroupsScreenProps {
  HeaderComponent?: React.ReactNode
  user: User | null
  userCreatedGroups: UserGroupsQueryOutput['userGroups']['docs']
  userJoinedGroups: UserGroupsQueryOutput['userGroups']['docs']
  userJoinedGroupsLoading: boolean
  userGroupJoinRequests: UserGroupJoinRequestsQueryOutput['userGroupJoinRequests']['docs']

  suggestedGroupsLoading: boolean
  suggestedGroups: SuggestedGroupsQueryOutput['suggestedGroups']['docs']

  refreshing: boolean
  onPullToRefresh: () => void
}

export function GroupsScreen(props: GroupsScreenProps) {
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  return (
    <MainLayout>
      <Meta title="Groups" />
      {props.HeaderComponent && props.HeaderComponent}
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={props.refreshing} onRefresh={props.onPullToRefresh} />
        }
      >
        {props.user && (
          <Container
            flex={1}
            ai="flex-start"
            jc="flex-start"
            spaceTop
            spaceBottom
            gap="$6"
            $gtMd={{
              gap: '$8',
            }}
          >
            {/* User created groups */}
            {props.user.userType === 'student' && (
              <Container size="sm" gutter>
                <YStack gap="$3.5" w="100%" flexShrink={1}>
                  <SectionTitle>Your groups</SectionTitle>

                  <Box gap="$2" pressable onPress={() => setCreateGroupModalOpen(true)}>
                    <Stack p="$2">
                      <PlusCircle size={32} color={colors.grey6} />
                    </Stack>
                    <SizableText
                      size="$5"
                      fow="600"
                      $gtMd={{
                        size: '$6',
                        fow: '600',
                      }}
                    >
                      Create new group
                    </SizableText>
                  </Box>

                  {props.userCreatedGroups.length === 0 ? (
                    <SizableText size="$3">You haven't created any groups yet</SizableText>
                  ) : (
                    props.userCreatedGroups.map((userGroup) => (
                      <GroupCardBasic
                        key={`Created:${userGroup.group.id}`}
                        id={userGroup.group.id}
                        name={userGroup.group.name}
                        visibility={userGroup.group.visibility}
                        totalMembers={userGroup.group.totalMembers || 0}
                        profileImage={{
                          url: userGroup.group.profileImage?.sizes?.medium?.url || '',
                        }}
                        href={`/groups/${userGroup.group.id}`}
                      />
                    ))
                  )}
                </YStack>
              </Container>
            )}

            {/* User joined groups */}
            <Container size="sm" gutter>
              <YStack gap="$3.5" w="100%" flexShrink={1}>
                <SectionTitle>Groups you joined</SectionTitle>

                {props.userJoinedGroups.length === 0 && (
                  <GroupNoResults
                    title="You haven't joined any groups yet"
                    description="Join a group to get started"
                  />
                )}

                {props.userJoinedGroups.length > 0 &&
                  props.userJoinedGroups.map((userGroup) => (
                    <GroupCardBasic
                      key={`Joined:${userGroup.group.id}`}
                      id={userGroup.group.id}
                      name={userGroup.group.name}
                      totalMembers={userGroup.group.totalMembers || 0}
                      visibility={userGroup.group.visibility}
                      profileImage={{
                        url: userGroup.group.profileImage?.sizes?.medium?.url || '',
                      }}
                      href={`/groups/${userGroup.group.id}`}
                    />
                  ))}

                {props.userJoinedGroupsLoading && !props.userJoinedGroups && (
                  <XStack w="100%" jc="center" ai="center" h={100}>
                    <Spinner color="$black" />
                  </XStack>
                )}
              </YStack>
            </Container>

            {/* Suggested groups */}
            {props.suggestedGroups?.length > 0 && (
              <Container size="sm" gutter flex={1}>
                <YStack gap="$3.5" w="100%" flex={1}>
                  <SectionTitle>Discover groups</SectionTitle>

                  <YStack gap="$3.5">
                    {props.suggestedGroups.map((group) => {
                      const { isMember, isAdmin } = hasJoinedGroup({
                        userGroups: props.userJoinedGroups,
                        groupId: group.id,
                      })
                      const pendingRequest = hasPendingGroupMembershipRequest({
                        groupJoinRequests: props.userGroupJoinRequests,
                        groupId: group.id,
                      })
                      return (
                        <GroupCardHorizontal
                          key={group.id}
                          id={group.id}
                          name={group.name}
                          visibility={group.visibility as GroupVisibility}
                          totalMembers={group?.totalMembers || 0}
                          href={`/groups/${group.id}`}
                          canJoin={!!props.user && canJoinGroup({ group, user: props.user })}
                          hasJoined={isMember}
                          isAdmin={isAdmin}
                          hasPendingRequest={pendingRequest}
                          canLeave={isMember && !isAdmin && canLeaveGroup({ group })}
                          profileImage={{
                            url: group.profileImage?.sizes?.medium?.url || '',
                            alt: group.name,
                          }}
                        />
                      )
                    })}
                  </YStack>
                </YStack>
              </Container>
            )}

            <GroupCreationWizard
              user={props.user}
              open={createGroupModalOpen}
              onOpenChange={setCreateGroupModalOpen}
            />
          </Container>
        )}
        {!props.user && (
          <Container flex={1} py="$3.5" gap="$2" gutter size="sm">
            <NoAccess title="You must Sign In or Sign Up to Student Saviour to access your groups" />
          </Container>
        )}
      </ScrollView>
    </MainLayout>
  )
}
